import React, { useMemo, useCallback, useState, useEffect } from "react";
import TableContainer from "../../components/Common/TableContainer.js";
import Breadcrumbs from "../../components/Common/Breadcrumb.js";
import { Card, CardBody, Button, Container } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import useFetchData from "../../custom/Utils/UseFetchDataUtils.js";
import usePagination from "../../custom/Utils/UsePaginationUtils.js";
import useListFilters from "../../custom/Utils/UseListFiltersUtils.js";
import { listPageData } from "./TechnicianStockReturnFile.js";
import {
  areAllValuesIdentical,
  smartSort,
} from "../../custom/Utils/ColumnUtils.js";

const TechnicianStockReturnList = () => {
  const navigate = useNavigate();
  const { payload, navigatePage, headerName, endpoint, searchfields, filterConfig } =
    listPageData();
  const initialPayload = useMemo(() => payload, []);

  // pagination
  const {
    page,
    pageSize,
    nextPage,
    previousPage,
    lastPage,
    firstPage,
    handlePageSizeChange,
  } = usePagination();

  // list filters (config comes from TechnicianStockReturnFile.js)
  const {
    filters,
    filterValues,
    handleFilterChange,
    resetFilters,
    filterWhereCondition,
    appliedFilterCount,
  } = useListFilters(filterConfig);

  // fetch data using the provided payload, page, and page size
  const { data, isLoading, setSearchKeyword, totalCount } = useFetchData(
    initialPayload,
    page,
    pageSize,
    endpoint,
    searchfields,
    { filterWhere: filterWhereCondition, onResetPage: firstPage }
  );
  // const [dataForExport, setDataForExport] = useState([]);

  // handle create action
  const handleCreate = () => navigate(navigatePage.create);

  const handleEdit = (id) => {
    navigate(`/technician-stock-return-create/${id}`);
  };
  const handleView = useCallback(
    (id) => {
      navigate(navigatePage.view + "/" + id);
    },
    [navigate, navigatePage.view],
  );

  // columns data
  const columns = useMemo(
    () => [
      {
        Header: "SR.NO",
        accessor: "index",
        accessor: (row) => page * pageSize + row.index,
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "50px" }}>{value}</div>,
      },

      {
        Header: "ENGINEER NAME",
        accessor: (row) =>
          row?.technician &&
          row?.technician.first_name &&
          row?.technician?.last_name
            ? `${row?.technician?.first_name} ${row?.technician?.last_name}`
            : "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "GMDS CODE",
        accessor: (row) =>
          row?.material ? row?.material?.gmds_code || row?.material?.name : "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "ITEM DESCRIPTION",
        accessor: (row) => row?.material?.descriptions || "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "200px" }}>{value}</div>,
      },
      {
        Header: "AVAILABLE QUANTITY",
        accessor: (row) => (row?.available_qty ? row?.available_qty : "-"),
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "RETURN QUANTITY",
        accessor: (row) => (row?.return_qty ? row?.return_qty : "-"),
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "ITEM LOCATION",
        accessor: (row) => row?.item_location || "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },

      {
        Header: "STATUS",
        accessor: "is_active",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        disableSortBy: areAllValuesIdentical(data, "is_active"),
        Cell: ({ value }) => (
          <div style={{ width: "100px" }}>
            {value === false || value === 0 ? (
              <span className="badge bg-danger-subtle text-danger text-uppercase">
                In-active
              </span>
            ) : (
              <span className="badge bg-success-subtle text-success text-uppercase">
                Active
              </span>
            )}
          </div>
        ),
      },
      {
        Header: "ACTION",
        accessor: "actions",
        disableFilters: true,
        filterable: false,
        Cell: ({ row }) => (
          <div style={{ width: "150px", display: "flex" }}>
            {/* <div
              onClick={() => {
                handleEdit(row.original.id);
              }}
            >
              <Link className="me-3 text-primary">
                <i className="mdi mdi-pencil font-size-18"></i>
              </Link>
            </div> */}
            <div
              onClick={() => {
                handleView(row.original.id);
              }}
            >
              <Link className="me-3 text-primary">
                <i className="mdi mdi-eye font-size-18"></i>
              </Link>
            </div>
          </div>
        ),
      },
    ],
    [page, pageSize, data],
  );

  // handle page size change callback
  const handlePageSizeChangeWrapper = useCallback(
    (newSize) => {
      handlePageSizeChange(newSize, totalCount);
    },
    [totalCount],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <Breadcrumbs title={headerName + " List"} breadcrumbItems={[]} />
            <Button
              color="primary"
              className="waves-effect waves-light me-1"
              onClick={handleCreate}
              style={{ marginLeft: "auto", background: "#006eb9" }}
            >
              Create {headerName}
            </Button>
          </div>

          <Card>
            <CardBody>
              <TableContainer
                columns={columns || []}
                data={data || []}
                isLoading={isLoading}
                onSearchInputData={setSearchKeyword}
                pageIndexProp={page}
                onPageSizeChange={handlePageSizeChangeWrapper}
                nextToPage={nextPage}
                lastToPage={() => lastPage(totalCount)}
                previousToPage={previousPage}
                previousFirstToPage={firstPage}
                totalCount={totalCount}
                customPageSize={pageSize}
                filters={filters}
                filterValues={filterValues}
                onFilterChange={handleFilterChange}
                onFilterReset={resetFilters}
                appliedFilterCount={appliedFilterCount}
                // exportData={exportData}
                // exportFileName={exportFileName}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TechnicianStockReturnList;
